// zpost-app/src/components/ensapp/IdentityDashboard.js
import React, { useState, useEffect } from 'react';

// All components now imported and active
import DomainMinter from './DomainMinter';
import PrimaryNameManager from './PrimaryNameManager';
import ProfileRecordsManager from './ProfileRecordsManager';
import AvatarManager from './AvatarManager';
import AddressLinker from './AddressLinker';
import WhoIsWho from './WhoIsWho';

const IdentityDashboard = ({
  integrationEnabled,
  receivedAddress,
  onDomainMinted,
  identityAuth,
  logoutIdentity,
  sendTransactionToWallet,
  awaitWalletResponse,
  walletPopupRef = null,
  walletIsReady = null,
  zpostProvider = null,
  ensureSessionToken = null
}) => {
  const [activeTab, setActiveTab] = useState('mint');

  console.log('🌐 IdentityDashboard MOUNTED - Active tab:', activeTab);
  console.log('🔐 IdentityAuth state FROM PROPS:', identityAuth);

  useEffect(() => {
    console.log('🔍 DEBUG: Components loaded status:');
    console.log('   - DomainMinter:', typeof DomainMinter !== 'undefined' ? '✅ LOADED' : '❌ NOT FOUND');
    console.log('   - PrimaryNameManager:', typeof PrimaryNameManager !== 'undefined' ? '✅ LOADED' : '❌ NOT FOUND');
    console.log('   - ProfileRecordsManager:', typeof ProfileRecordsManager !== 'undefined' ? '✅ LOADED' : '❌ NOT FOUND');
    console.log('   - AvatarManager:', typeof AvatarManager !== 'undefined' ? '✅ LOADED' : '❌ NOT FOUND');
    console.log('   - AddressLinker:', typeof AddressLinker !== 'undefined' ? '✅ LOADED' : '❌ NOT FOUND');
    console.log('   - WhoIsWho:', typeof WhoIsWho !== 'undefined' ? '✅ LOADED' : '❌ NOT FOUND');
  }, []);

  const handleTitleClick = () => setActiveTab('mint');
  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setActiveTab('mint');
    }
  };

  return (
    <div className="dashboard-container fade-in">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="user-info">
          <h1
            role="button"
            tabIndex={0}
            onClick={handleTitleClick}
            onKeyDown={handleTitleKeyDown}
            title="Go to Home (Mint Domain)"
            style={{ cursor: 'pointer', userSelect: 'none' }}
            aria-label="Identity Dashboard Home"
          >
            Identity Dashboard
          </h1>

          <div className="user-details">
            <span className="ens-name">
              {identityAuth.ensName || 'No .zpost.eth domain'}
            </span>
            <span className="wallet-address">
              {identityAuth.walletAddress ?
                `${identityAuth.walletAddress.slice(0, 8)}...${identityAuth.walletAddress.slice(-6)}` :
                'Not connected'}
            </span>
          </div>
        </div>
        <button
          onClick={logoutIdentity}
          className="btn btn-dark-red logout-btn"
        >
          Disconnect
        </button>
      </div>

      {/* Navigation Tabs */}
      <div className="dashboard-tabs">
        <button
          className={`tab-btn ${activeTab === 'mint' ? 'active' : ''}`}
          onClick={() => setActiveTab('mint')}
        >
          Mint Domain
        </button>
        <button
          className={`tab-btn ${activeTab === 'primary' ? 'active' : ''}`}
          onClick={() => setActiveTab('primary')}
        >
          Primary Name
        </button>
        <button
          className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile Records
        </button>
        <button
          className={`tab-btn ${activeTab === 'avatar' ? 'active' : ''}`}
          onClick={() => setActiveTab('avatar')}
        >
          Avatar & Header
        </button>
        <button
          className={`tab-btn ${activeTab === 'linker' ? 'active' : ''}`}
          onClick={() => setActiveTab('linker')}
        >
          Address Linker
        </button>
        <button
          className={`tab-btn ${activeTab === 'whoiswho' ? 'active' : ''}`}
          onClick={() => setActiveTab('whoiswho')}
        >
          Who Is Who
        </button>
      </div>

      {/* Tab Content */}
      <div className="dashboard-content">
        {/* Mint Domain Tab */}
        {activeTab === 'mint' && (
          <div className="domain-minter">
            <DomainMinter
              integrationEnabled={integrationEnabled}
              receivedAddress={receivedAddress || identityAuth.walletAddress}
              onDomainMinted={onDomainMinted}
              identityAuth={identityAuth}
              sendTransactionToWallet={sendTransactionToWallet}
              awaitWalletResponse={awaitWalletResponse}
              zpostProvider={zpostProvider}
            />

            <div className="status-info status-message">
              ✅ DomainMinter component is ACTIVE
            </div>
          </div>
        )}

        {/* Primary Name Tab */}
        {activeTab === 'primary' && (
          <div className="primary-name-section">
            <h3>Set Primary Name</h3>
            <p className="text-secondary">Choose which domain represents your primary identity</p>

            <PrimaryNameManager
              identityAuth={identityAuth}
              sendTransactionToWallet={sendTransactionToWallet}
              awaitWalletResponse={awaitWalletResponse}
              zpostProvider={zpostProvider}
            />

            <div className="status-info status-message">
              ✅ PrimaryNameManager is ACTIVE
            </div>
          </div>
        )}

        {/* Profile Records Tab */}
        {activeTab === 'profile' && (
          <div className="record-editor">
            <h3>Manage Profile Records</h3>
            <p className="text-secondary">Add social links and profile information to your domain</p>

            <ProfileRecordsManager
              identityAuth={identityAuth}
              awaitWalletResponse={awaitWalletResponse}
              sendTransactionToWallet={sendTransactionToWallet}
              walletPopupRef={walletPopupRef}
              walletIsReady={walletIsReady}
              zpostProvider={zpostProvider}
              ensureSessionToken={ensureSessionToken}
            />

            <div className="status-info status-message">
              ✅ ProfileRecordsManager is ACTIVE
            </div>
          </div>
        )}

        {/* Avatar Tab */}
        {activeTab === 'avatar' && (
          <div className="avatar-section">
            <h3>Manage Avatar</h3>
            <p className="text-secondary">Set a profile picture for your identity</p>

            <AvatarManager
              identityAuth={identityAuth}
              sendTransactionToWallet={sendTransactionToWallet}
              awaitWalletResponse={awaitWalletResponse}
              zpostProvider={zpostProvider}
            />

            <div className="status-info status-message">
              ✅ AvatarManager is now ACTIVE
            </div>
          </div>
        )}

        {/* Address Linker Tab */}
        {activeTab === 'linker' && (
          <div className="record-editor">
            <h3>Address Linker</h3>
            <p className="text-secondary">Link multiple addresses to your identity</p>

            <AddressLinker
              identityAuth={identityAuth}
              sendTransactionToWallet={sendTransactionToWallet}
              awaitWalletResponse={awaitWalletResponse}
              walletPopupRef={walletPopupRef}
              walletIsReady={walletIsReady}
              zpostProvider={zpostProvider}
            />

            <div className="status-info status-message">
              ✅ AddressLinker is ACTIVE
            </div>
          </div>
        )}

        {/* Who Is Who Tab */}
        {activeTab === 'whoiswho' && (
          <div className="who-is-who-section">
            <h3>Who Is Who</h3>
            <p className="text-secondary">Look up any ENS domain to see profile information and social links</p>

            <WhoIsWho
              identityAuth={identityAuth}
              zpostProvider={zpostProvider}
            />

            <div className="status-info status-message">
              ✅ WhoIsWho component is ACTIVE
            </div>
            
            {/* Help Text */}
            <div className="content-card" style={{ marginTop: '2rem' }}>
              <h4 style={{ color: 'var(--gold)', marginBottom: '0.5rem' }}>ℹ️ How to use Who Is Who:</h4>
              <ul style={{ 
                textAlign: 'left', 
                margin: '0.5rem 0',
                paddingLeft: '1.5rem',
                color: 'var(--text-secondary)',
                fontSize: '0.9rem'
              }}>
                <li>Enter <strong>any ENS domain</strong> (like <code>web3reg.eth</code> or your own domain)</li>
                <li>View the <strong>resolved Ethereum address</strong> and profile information</li>
                <li>See <strong>social media links</strong>, websites, and contact details</li>
                <li>Check for <strong>multi-coin addresses</strong> (BTC, LTC, DOGE, etc.)</li>
                <li><strong>Save domains</strong> to your contacts for quick access later</li>
                <li><strong>Share profiles</strong> with others directly from the app</li>
                <li><strong>Verify addresses</strong> on Etherscan for trust confirmation</li>
              </ul>
              
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                background: 'rgba(255, 215, 0, 0.1)',
                borderRadius: '5px',
                borderLeft: '3px solid var(--gold)'
              }}>
                <p style={{ 
                  color: 'var(--gold)', 
                  fontSize: '0.9rem',
                  margin: '0'
                }}>
                  <strong>Pro Tips:</strong>
                </p>
                <ul style={{ 
                  textAlign: 'left', 
                  margin: '0.5rem 0 0 0',
                  paddingLeft: '1.5rem',
                  color: 'var(--text-secondary)',
                  fontSize: '0.85rem'
                }}>
                  <li>Look up your own domain to see how others view your profile</li>
                  <li>Save important contacts (like team members or partners)</li>
                  <li>Use for due diligence before transactions</li>
                  <li>Discover new people in the ZPost ecosystem</li>
                </ul>
              </div>
              
              <p style={{ 
                color: 'var(--text-secondary)', 
                fontSize: '0.8rem',
                marginTop: '1rem',
                textAlign: 'center',
                fontStyle: 'italic'
              }}>
                All ENS domain information is publicly available on the Ethereum blockchain
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Integration Status */}
      {integrationEnabled && (
        <div className="status-success status-message mt-20">
          🔗 Integration Mode: Connected to main application
        </div>
      )}
    </div>
  );
};

export default IdentityDashboard;
